"use client";

import { useState } from 'react';
import { DocumentTestCard } from './DocumentTestCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { InfoIcon, CheckCircle2, Zap, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

interface TurfAIIntegrationTestProps {
  onBack?: () => void;
}

export function TurfAIIntegrationTest({ onBack }: TurfAIIntegrationTestProps) {
  const [testResults, setTestResults] = useState<any[]>([]);
  
  const handleTestComplete = (results: any) => {
    setTestResults(prev => [...prev, {
      timestamp: new Date().toISOString(),
      results
    }]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-none shadow-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    {onBack && (
                      <button
                        onClick={onBack}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Back to Dashboard"
                      >
                        <ArrowLeft className="w-6 h-6" />
                      </button>
                    )}
                    <CardTitle className="text-3xl font-bold flex items-center gap-3">
                      <Zap className="w-8 h-8" />
                      TurfAI Integration Test
                    </CardTitle>
                  </div>
                  <CardDescription className="text-gray-100">
                    Test document workflows with the TurfAI platform
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary" className="bg-white/20 text-white">
                    v1.0
                  </Badge>
                  <Badge variant="secondary" className="bg-green-500/20 text-white">
                    Live
                  </Badge>
                </div>
              </div>
            </CardHeader>
          </Card>
        </motion.div>

        {/* Info Alert */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Alert className="border-blue-200 bg-blue-50">
            <InfoIcon className="h-4 w-4" />
            <AlertTitle>How to Test</AlertTitle>
            <AlertDescription className="space-y-2 mt-2">
              <p>1. Upload any document (Aadhaar, PAN, Passport, License, etc.)</p>
              <p>2. Select a workflow type:</p>
              <ul className="ml-6 list-disc">
                <li><strong>Classify Only:</strong> Identifies the document type</li>
                <li><strong>Extract Only:</strong> Extracts structured data (requires prompt selection)</li>
                <li><strong>Classify & Extract:</strong> Does both operations in sequence</li>
              </ul>
              <p>3. For extraction workflow: Choose a predefined prompt or create a custom one</p>
              <p>4. Click "Execute Workflow" to process the document</p>
            </AlertDescription>
          </Alert>
        </motion.div>

        {/* Test Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <DocumentTestCard 
            title="Document Workflow Tester"
            description="Upload any document to test classification, extraction, or both"
            onComplete={handleTestComplete}
          />
        </motion.div>

        {/* Test History */}
        {testResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Test History
                </CardTitle>
                <CardDescription>
                  Recent test executions ({testResults.length} total)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {testResults.slice(-3).reverse().map((test, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium">
                            Test #{testResults.length - index}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(test.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-green-600">
                          Success
                        </Badge>
                      </div>
                      <div className="mt-2">
                        <pre className="text-xs bg-white p-2 rounded overflow-x-auto">
                          {JSON.stringify(test.results, null, 2).substring(0, 200)}...
                        </pre>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center text-sm text-gray-500 pb-6"
        >
          <p>TurfAI Platform Integration • DayOne System</p>
          <p className="mt-1">Powered by AI Document Processing</p>
        </motion.div>
      </div>
    </div>
  );
}